import React, { useRef, useState, useEffect } from 'react'
import { GlobeBackground } from './HaloBackground'
import { motion, useScroll, useTransform } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useWaitlist } from '@/context/WaitlistContext'

const TimeUnit = ({ value, label }: { value: string, label: string }) => (
  <div className="flex flex-col items-center">
    <span className="font-mono text-lg sm:text-xl font-bold text-red-400">{value}</span>
    <span className="text-[10px] uppercase tracking-wider text-gray-400 mt-0.5">{label}</span>
  </div>
);

export const Hero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const [isMobile, setIsMobile] = useState(false);
  const { spotsLeft, timeLeft } = useWaitlist();

  // Split time into units
  const timeUnits = timeLeft.split(':').map((unit, index) => {
    const labels = ['days', 'hrs', 'min', 'sec'];
    return { value: unit, label: labels[index] };
  });

  // Handle mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToForm = () => {
    const formElement = document.getElementById('contact-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const content = (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-black to-black/20">
      <div className="relative z-10 container mx-auto px-4 py-12 sm:py-20">
        {/* Authority Bar - Mobile Optimized */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="flex justify-center mb-8 sm:mb-12"
        >
          {isMobile ? (
            <div className="px-8 py-3 bg-gradient-to-r from-purple-500/10 via-purple-400/10 to-purple-500/10 backdrop-blur-sm rounded-full border border-purple-400/20">
              <div className="text-purple-200 text-sm font-medium">Microsoft-Backed</div>
            </div>
          ) : (
            <div className="flex flex-row items-center gap-6 px-6 py-3 bg-gradient-to-r from-purple-500/10 via-purple-400/10 to-purple-500/10 backdrop-blur-sm rounded-full border border-purple-400/20">
              <div className="text-purple-200 text-sm font-medium">Microsoft-Backed</div>
              <div className="h-4 w-px bg-purple-400/20" />
              <div className="text-purple-200 text-sm font-medium">312% Revenue Impact</div>
              <div className="h-4 w-px bg-purple-400/20" />
              <div className="text-purple-200 text-sm font-medium">Used by Smart People</div>
            </div>
          )}
        </motion.div>

        <div className="text-center max-w-4xl mx-auto px-2 sm:px-4">
          {/* Main Headline */}
          <div className="overflow-hidden mb-4 sm:mb-6">
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.25, 0.1, 0, 1] }}
            >
              <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-bold tracking-tight leading-tight">
                <span className="inline-block">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1 }}
                    className="bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-300 to-white [text-shadow:0_0_40px_rgba(255,255,255,0.2)] relative"
                  >
                    Agentic
                  </motion.span>
                </span>
                {" "}
                <span className="inline-block">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.2 }}
                    className="bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-white to-purple-300 [text-shadow:0_0_40px_rgba(167,139,250,0.3)]"
                  >
                    AI
                  </motion.span>
                </span>
                {" "}
                <span className="inline-block">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1.4 }}
                    className="bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-300 to-white [text-shadow:0_0_40px_rgba(255,255,255,0.2)]"
                  >
                    Sales
                  </motion.span>
                </span>
              </h1>
            </motion.div>
          </div>
          
          {/* Value Proposition */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.6 }}
            className="mb-8 sm:mb-12"
          >
            <p className="text-lg sm:text-2xl md:text-3xl text-white/90 [text-shadow:0_0_20px_rgba(255,255,255,0.1)] px-4">
              Your entire sales pipeline on autopilot, Hands Free
            </p>
          </motion.div>

          {/* CTAs */}
          <motion.div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.8 }}
          >
            <button
              onClick={scrollToForm}
              className="w-full sm:w-auto relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-500 to-purple-400 hover:from-purple-400 hover:to-purple-500 text-white font-medium rounded-lg overflow-hidden group transition-all duration-300"
            >
              <span className="relative z-10 text-sm sm:text-base">
                {spotsLeft > 0 ? `Join Waitlist (${spotsLeft} spots left)` : 'Join Waitlist'}
              </span>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-300/20 to-white/20 animate-shimmer" />
              </div>
            </button>
            <div className="flex gap-3 items-center">
              {timeUnits.map((unit, index) => (
                <React.Fragment key={unit.label}>
                  <TimeUnit value={unit.value} label={unit.label} />
                  {index < timeUnits.length - 1 && (
                    <span className="text-gray-600 font-mono text-lg">:</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );

  return (
    <div ref={containerRef} className="relative">
      <GlobeBackground showFloatingMetrics={!isMobile}>
        {content}
      </GlobeBackground>
    </div>
  );
}

export default Hero;